// @ts-nocheck
import { ReactNode } from 'react';
import { Box } from '@mantine/core';
import { useMantineTheme, useMantineColorScheme } from '@mantine/core';
import { useReducedMotion } from '@/components/animations';
import { keyframes } from '@/components/animations/utils/keyframes';

const neonPulse = keyframes({
  '0%, 100%': { opacity: 1, boxShadow: 'var(--neon-shadow)' },
  '50%': { opacity: 0.8, boxShadow: 'var(--neon-shadow-dim)' },
});

const borderGlow = keyframes({
  '0%': { backgroundPosition: '0% 50%' },
  '50%': { backgroundPosition: '100% 50%' },
  '100%': { backgroundPosition: '0% 50%' },
});

export type NeonColor = 'cyan' | 'pink' | 'purple' | 'green' | 'orange' | 'multi';

interface NeonCardProps {
  children: ReactNode;
  /** Neon color theme */
  color?: NeonColor;
  /** Whether the glow is animated */
  animated?: boolean;
  /** Border thickness */
  borderWidth?: number;
  /** Card padding */
  padding?: 'sm' | 'md' | 'lg' | 'xl';
  /** Border radius */
  radius?: 'sm' | 'md' | 'lg' | 'xl';
  /** Glass morphism effect */
  glass?: boolean;
}

export default function NeonCard({
  children,
  color = 'cyan',
  animated = true,
  borderWidth = 2,
  padding = 'xl',
  radius = 'lg',
  glass = true,
}: NeonCardProps) {
  const theme = useMantineTheme();
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === 'dark';
  const reduceMotion = useReducedMotion();

  const paddingValues = {
    sm: theme.spacing.sm,
    md: theme.spacing.md,
    lg: theme.spacing.lg,
    xl: theme.spacing.xl,
  };

  const radiusValues = {
    sm: theme.radius.sm,
    md: theme.radius.md,
    lg: theme.radius.lg,
    xl: theme.radius.xl,
  };

  const getNeonColors = () => {
    switch (color) {
      case 'cyan':
        return {
          primary: '#00f5ff',
          secondary: '#00d4ff',
          shadow: 'rgba(0, 245, 255, 0.5)',
          shadowDim: 'rgba(0, 245, 255, 0.3)',
        };
      case 'pink':
        return {
          primary: '#ff00ff',
          secondary: '#ff66ff',
          shadow: 'rgba(255, 0, 255, 0.5)',
          shadowDim: 'rgba(255, 0, 255, 0.3)',
        };
      case 'purple':
        return {
          primary: '#bf00ff',
          secondary: '#9933ff',
          shadow: 'rgba(191, 0, 255, 0.5)',
          shadowDim: 'rgba(191, 0, 255, 0.3)',
        };
      case 'green':
        return {
          primary: '#00ff66',
          secondary: '#33ff99',
          shadow: 'rgba(0, 255, 102, 0.5)',
          shadowDim: 'rgba(0, 255, 102, 0.3)',
        };
      case 'orange':
        return {
          primary: '#ff6600',
          secondary: '#ff9933',
          shadow: 'rgba(255, 102, 0, 0.5)',
          shadowDim: 'rgba(255, 102, 0, 0.3)',
        };
      case 'multi':
        return {
          primary: '#ff00ff',
          secondary: '#00ffff',
          shadow: 'rgba(255, 0, 255, 0.4)',
          shadowDim: 'rgba(0, 255, 255, 0.4)',
        };
      default:
        return {
          primary: '#00f5ff',
          secondary: '#00d4ff',
          shadow: 'rgba(0, 245, 255, 0.5)',
          shadowDim: 'rgba(0, 245, 255, 0.3)',
        };
    }
  };

  const colors = getNeonColors();

  const glassStyles = glass ? {
    backgroundColor: isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(12px)',
  } : {
    backgroundColor: isDark ? theme.colors.dark[8] : theme.colors.gray[0],
  };

  const getBorderGradient = () => {
    if (color === 'multi') {
      return `linear-gradient(90deg, ${colors.primary}, ${colors.secondary}, #00ff66, #ffff00, ${colors.primary})`;
    }
    return `linear-gradient(90deg, ${colors.primary}, ${colors.secondary}, ${colors.primary})`;
  };

  return (
    <Box
      style={{
        position: 'relative',
        padding: borderWidth,
        borderRadius: radiusValues[radius],
        background: getBorderGradient(),
        backgroundSize: '200% 200%',
        animation: animated && !reduceMotion ? `${borderGlow} 3s ease infinite` : undefined,
        '--neon-shadow': `0 0 10px ${colors.shadow}, 0 0 20px ${colors.shadow}, 0 0 30px ${colors.shadow}`,
        '--neon-shadow-dim': `0 0 5px ${colors.shadowDim}, 0 0 10px ${colors.shadowDim}`,
        boxShadow: `0 0 15px ${colors.shadow}, 0 0 30px ${colors.shadowDim}`,
      } as React.CSSProperties}
    >
      <Box
        style={{
          ...glassStyles,
          borderRadius: `calc(${radiusValues[radius]} - ${borderWidth}px)`,
          padding: paddingValues[padding],
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
